import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { socialApi, assertMessageAcknowledgement } from "@bw/shared/api";
import type {
  MessageCreate,
  MessageResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  conversationAuthorityVersion,
  refreshConversationAuthorityAfterMutationError,
} from "../../../common/hooks/conversationCacheAuthority";
import {
  installConfirmedMessage,
  reconcileMessagePages,
} from "./messageCacheAuthority";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

export interface MessageSendVars {
  content: string;
  shared_post_id?: string;
  attachment_ids?: string[];
}

interface PendingCommand {
  signature: string;
  command: MessageCreate;
}

function normalizedCommand(
  vars: MessageSendVars,
): Omit<MessageCreate, "idempotency_key"> {
  return {
    content: vars.content.trim(),
    shared_post_id: vars.shared_post_id ?? null,
    ...(vars.attachment_ids?.length
      ? { attachment_ids: vars.attachment_ids }
      : {}),
  };
}

/**
 * Replay-safe message mutation. An ambiguous retry of the same normalized
 * command reuses its UUID; changing the draft starts a new command. The key is
 * rotated only after confirmed success or an explicitly different payload.
 */
export function useSendMessage(conversationId: string, communityId?: string) {
  const queryClient = useQueryClient();
  const pendingRef = useRef<PendingCommand | null>(null);

  return useMutation<MessageResponse, unknown, MessageSendVars, string>({
    onMutate: () => conversationAuthorityVersion(queryClient, conversationId),
    onError: (error) =>
      refreshConversationAuthorityAfterMutationError(
        queryClient,
        conversationId,
        communityId,
        error,
      ),
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: async (vars) => {
      const normalized = normalizedCommand(vars);
      const signature = JSON.stringify({ conversationId, ...normalized });
      if (pendingRef.current?.signature !== signature) {
        pendingRef.current = {
          signature,
          command: {
            ...normalized,
            idempotency_key: globalThis.crypto.randomUUID(),
          },
        };
      }
      const response = await socialApi.sendMessage(conversationId, pendingRef.current.command);
      assertMessageAcknowledgement(response, conversationId);
      return response;
    },
    onSuccess: (response, _vars, authorityVersion) => {
      pendingRef.current = null;
      if (
        authorityVersion !==
        conversationAuthorityVersion(queryClient, conversationId)
      )
        return;
      installConfirmedMessage(queryClient, conversationId, response);
      void reconcileMessagePages(queryClient, conversationId);
      void queryClient.invalidateQueries({
        queryKey: communityId
          ? queryKeys.social.communityChannels(communityId)
          : queryKeys.social.conversations(),
      });
    },
  });
}
